import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  Inject,
  OnInit,
  OnChanges,
  OnDestroy,
  ElementRef,
  HostBinding,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { TypeOfPager, TypePositionOfArrow } from 'cms/cms-shared/common-settings-styles/common-settings-styles.models';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductWithCategory } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { CMSEvent, CMSProductCarouselEvent } from 'render/models/cms-event.model';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { RenderStorageProductsService } from 'render/render-storage/render-storage-products/render-storage-products.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable, BehaviorSubject, of, iif } from 'rxjs';
import {
  switchMap, distinctUntilChanged, filter, map, takeUntil, tap
} from 'rxjs/operators';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { InditexWindow } from 'shared/models/inditex-window.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import SwiperCore, { SwiperOptions, Pagination, Autoplay, Scrollbar } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { PaginationOptions } from 'swiper/types';

import { BorderRadiusType, ImageLocation, PieceProductCarouselSettings, PieceProductCarouselSource, PieceProductCarouselViewStyle } from './models/piece-product-carousel.model';
import { styleUrls, templateUrl } from './piece-product-carousel-render.component.paths';
import {
  PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS,
  PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS,
  PRODUCT_CAROUSEL_DEFAULT_PRODUCT_NAME_FORMAT,
} from './piece-product-carousel.definition';


const IPOC_PRODUCTS_INITIALIZED = 'arrayProductsChanged';
const PRODUCT_IMAGE_CLASS = 'product-single';
const EXTERNAL_SOURCES: PieceProductCarouselSource[] = ['externalRecommendations', 'yodaRecommendedForYou', 'yodaTrends'];
// TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
const CATEGORIES_SOURCES: PieceProductCarouselSource[] = ['category', 'categoryV2', 'seoId'];

SwiperCore.use([Pagination, Autoplay, Scrollbar]);

@Component({
  selector: 'piece-product-carousel-render',
  templateUrl,
  styleUrls,
  encapsulation: ViewEncapsulation.ShadowDom
})

export class PieceProductCarouselRenderComponent
  extends BaseRenderComponent<PieceProductCarouselSettings>
  implements OnInit, OnChanges, OnDestroy {

  @ViewChild(SwiperComponent) public swiperComponent?: SwiperComponent;

  public displayComponentArrows: boolean = false;
  public displayComponentPager: boolean = false;
  public sourceType: PieceProductCarouselSource = 'category';

  public sourceType$ = new BehaviorSubject<PieceProductCarouselSource>(this.sourceType);
  public sourceId$ = new BehaviorSubject<string | null>(null);

  public products$!: Observable<Product[] | ProductWithCategory[] | undefined>;

  public imageClass = PRODUCT_IMAGE_CLASS;

  public swiperConfig: SwiperOptions = {};

  private displayedProductIds: string[] = [];

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly elementRef: ElementRef,
    private readonly productsService: ProductsService,
    private readonly contextService: ContextService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly renderer: Renderer2,
    private readonly localStorageService: RenderStorageProductsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly cmsRenderEventsService: CMSRenderEventsService
  ) {
    super();
  }

  public get imageBorderRadiusType(): BorderRadiusType {
    return this.piece?.settings?.styles?.imageBorderRadiusType ?? 'straight';
  }

  public get imageBorderRadius(): string {
    return this.piece?.settings?.styles?.imageBorderRadius ?? '0pt';
  }

  public get scrollMode(): 'free' | 'pager' {
    return this.piece?.settings?.general?.scrollMode ?? 'free';
  }

  @HostBinding('class.scroll-free')
  public get isScrollFree() {
    return this.scrollMode === 'free';
  }

  @HostBinding('class.scroll-pager')
  public get isScrollPager() {
    return this.scrollMode === 'pager';
  }

  public get viewStyle(): PieceProductCarouselViewStyle {
    return this.piece?.settings?.general?.viewStyle ?? 'grid-image';
  }

  @HostBinding('class.view-style-grid')
  public get isViewStyleGrid() {
    return this.viewStyle === 'grid-image';
  }

  @HostBinding('class.view-style-silhouette')
  public get isViewStyleSilhouette() {
    return this.viewStyle === 'silhouette-image';
  }

  @HostBinding('class.view-style-double')
  public get isViewStyleDouble() {
    return this.viewStyle === 'double-image';
  }

  public get pagerStyle(): TypeOfPager {
    return this.piece?.settings?.general?.pagerStyle ?? 'dot';
  }

  public get arrowPosition(): TypePositionOfArrow {
    return this.piece?.settings?.general?.arrowPosition ?? 'inside-left-right';
  }

  @HostBinding('class.pager-style-dot')
  public get isPagerStyleDot() {
    return this.pagerStyle === 'dot';
  }

  @HostBinding('class.pager-style-box')
  public get isPagerStyleBox() {
    return this.pagerStyle === 'box';
  }

  @HostBinding('class.pager-style-solid')
  public get isPagerStyleSolid(): boolean {
    return this.pagerStyle === 'solid';
  }

  @HostBinding('class.pager-style-dashed')
  public get isPagerStyleDashed(): boolean {
    return this.pagerStyle === 'dashed';
  }

  @HostBinding('class.dark-theme')
  public get isDarkTheme() {
    return this.piece?.settings?.general?.pagerStyleTheme === 'dark-theme';
  }

  @HostBinding('class.light-theme')
  public get isLightTheme() {
    return this.piece?.settings?.general?.pagerStyleTheme === 'light-theme';
  }

  public get isFirstSlide(): boolean {
    return !!this.swiperComponent?.swiperRef.isBeginning && !this.piece?.settings?.general.loop;
  }

  public get isLastSlide(): boolean {
    return !!this.swiperComponent?.swiperRef.isEnd && !this.piece?.settings?.general.loop;
  }

  public get maxProducts(): number | undefined {
    return Number(this.piece?.settings?.general?.maxProducts) || undefined;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public ngOnInit(): void {
    this.initConfig();

    this.window.inditex?.onReady(() => {
      this.window.inditex!.notifications.addHandler(IPOC_PRODUCTS_INITIALIZED, () => {
        if (EXTERNAL_SOURCES.includes(this.sourceType)) {
          this.sourceType$.next(this.sourceType);
          this.window.inditex!.notifications.clearHandlers(IPOC_PRODUCTS_INITIALIZED);
        }
      });
    });

    const productsFromCategory$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap((categoryId) => this.productsService.categoryV3Products(categoryId, true, this.maxProducts))
    );

    const productsFromBundle$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      switchMap((productId) => {
        return this.productsService.productDetailOrNull(productId).pipe(
          map((product) => (isDefined(product) ? product.bundleProducts : []))
        );
      })
    );

    const productsFromList$ = this.sourceId$.pipe(
      filter(isDefined),
      distinctUntilChanged(),
      map((sourceId) => this.sanitizeProducts(sourceId)),
      switchMap((productIds: string) => this.productsService.productsDetailArray(productIds))
    );

    this.products$ = this.sourceType$.pipe(
      takeUntil(this.clearSubscriptions$),
      filter(isDefined),
      distinctUntilChanged(
        (prev: PieceProductCarouselSource, next: PieceProductCarouselSource) => prev === next
          && !EXTERNAL_SOURCES.some((source) => [prev, next].includes(source))
      ),
      switchMap((sourceType) => {
        this.sourceType = sourceType;
        // TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
        if (CATEGORIES_SOURCES.includes(sourceType)) {
          return productsFromCategory$;
        }

        if (sourceType === 'productBundle') {
          return productsFromBundle$;
        }

        if (sourceType === 'productList') {
          return productsFromList$;
        }

        if (EXTERNAL_SOURCES.includes(sourceType)) {
          return this.externalProducts();
        }

        if (sourceType === 'localLastSeenProducts') {
          let latestsViewed$ = this.localStorageService.getLastViewed();
          if (this.piece?.settings?.general?.sortDirection === 'oldFirst') {
            latestsViewed$ = latestsViewed$.pipe(map(products => products.reverse()));
          }
          return latestsViewed$;
        }

        return of([] as Product[]);
      }),
      map((products) => {
        if (this.piece?.settings?.general.displayFilterSeasonalProducts) {
          return this.filterSeasonalProducts(products);
        }
        return products;
      }),
      map((products = []) => products.slice(0, this.maxProducts)),
      tap((products) => {
        this.displayedProductIds = products.map((product) => product.id);
      })
    );

    this.breakpointObserver.observe(
      this.renderConfigService.config.breakpoints.map((breakpoint) => breakpoint.bp)
    ).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      this.onMediaChange();
    });
  }

  public ngOnChanges(): void {
    if (isDefined(this.piece) && isDefined(this.piece.settings) && isDefined(this.piece.settings.general)) {
      const settings = this.piece.settings.general;
      const styles = this.piece.settings.styles;
      const element = this.elementRef.nativeElement;

      if (isDefined(settings.sourceType)) {
        this.sourceType$.next(settings.sourceType);
      }

      if (isDefined(settings.sourceId)) {
        iif(
          () => settings.sourceType === 'seoId',
          this.getCategoryIdBySeoId$(settings.sourceId),
          of(settings.sourceId)
        ).subscribe((newSourceId) => this.sourceId$.next(newSourceId ?? null));
      }

      this.imageClass = `${PRODUCT_IMAGE_CLASS} ${this.imageBorderRadiusType}`;
      this.renderer.setStyle(element, '--image-border-radius', styles.imageBorderRadius, RendererStyleFlags2.DashCase);

      this.displayComponentArrows = settings.displayComponentArrows;
      this.displayComponentPager = settings.displayComponentPager;

      const { objectFit, objectPosition } = styles;

      if (!!objectFit) {
        this.renderer.setStyle(element, '--imageObjectFit', `${objectFit}`, 2);
      }

      if (!!objectPosition) {
        this.renderer.setStyle(element, '--imageObjectPosition', `${objectPosition}`, 2);
      }
    }
  }

  public get gridSize(): { numberOfColumns: number; numberOfRows: number } {
    if (this.piece?.settings?.general) {
      const gridSize = {
        numberOfColumns: +(this.piece.settings.general.numberOfColumns || PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS),
        numberOfRows: (this.piece.settings.general.numberOfRows || PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS)
      };

      if (this.isMobile) {
        if (this.piece.settings.general.numberOfColumnsMobile) {
          gridSize.numberOfColumns = this.piece.settings.general.numberOfColumnsMobile;
        }
        if (this.piece.settings.general.numberOfRowsMobile) {
          gridSize.numberOfRows = this.piece.settings.general.numberOfRowsMobile;
        }
      }
      return gridSize;
    }

    return { numberOfColumns: PRODUCT_CAROUSEL_DEFAULT_NUMBER_COLUMNS, numberOfRows: PRODUCT_CAROUSEL_DEFAULT_NUMBER_ROWS };
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.window.inditex?.notifications.clearHandlers(IPOC_PRODUCTS_INITIALIZED);
  }

  public get showSliderHeader(): boolean {
    let shouldShow = false;
    if (this.piece && this.piece.settings && this.piece.settings.general) {
      shouldShow = (this.piece.settings.general.showTitle
        && this.piece.settings.general.textGridTitle
        && this.piece.settings.general.textGridTitle.length > 0)
        || this.piece.settings.general.arrowPosition === 'header-right';
    }
    return shouldShow;
  }

  public isVisibleTitle(): boolean {
    return !!this.piece?.settings?.general?.showTitle;
  }

  public isVisibleName(): boolean {
    return !!this.piece?.settings?.general?.displayProductNames;
  }

  public isVisiblePrice(): boolean {
    return !!this.piece?.settings?.general?.displayProductPrices;
  }

  public isVisibleCart(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCartToggle;
  }

  public isVisibleCartDeprecated(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCart;
  }

  public isVisibleButtonLink(): boolean {
    return !!this.piece?.settings?.general?.displayButtonLink;
  }

  public getTextProductName(productName: string): string {
    if (this.piece?.settings?.general?.productNamesTextFormat) {
      const template = this.piece.settings.general.productNamesTextFormat;
      return template.replace(/{{productName}}/g, productName);
    }
    return PRODUCT_CAROUSEL_DEFAULT_PRODUCT_NAME_FORMAT.replace(/{{productName}}/g, productName);
  }

  public getProductImageData(product: Product): LazyDataInterface | undefined {
    const width = 600;
    const defaultImageLocation = 'category_page';
    const imageLocation = this.piece?.settings?.general.imageLocation || defaultImageLocation;
    const location: ImageLocation = imageLocation;

    const imageView = this.getViewStyle(this.piece?.settings?.general.viewStyle);

    const media = ProductsHelpers.firstImageForFirstColor(product.colors, location, width, imageView);
    const imageData = this.assetsSharedService.productImageData(
      media,
      width,
      this.renderConfigService.config,
      this.piece?.settings?.general?.impolicy
    );

    return imageData;
  }

  public getProductLink(product: Product | ProductWithCategory, index: number): Link {
    const link: Link = {
      type: 'product',
      pieceName: this.piece?.name,
      productId: product.id,
      ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier }),
      ...(this.piece?.settings?.general.enableUnifiedUrl && { enableUnifiedUrl: this.piece.settings.general.enableUnifiedUrl })
    };

    if ('categoryId' in product) {
      link.categoryId = product.categoryId;
    }

    if (product.productPartNumberDetails) {
      link.productPartNumberDetails = product.productPartNumberDetails;
    }

    if (EXTERNAL_SOURCES.includes(this.sourceType) && this.window.inditex?.iPOCRecommendationToken) {
      link.extraQueryParams = [{
        key: 'rtoken',
        value: `${this.window.inditex?.iPOCRecommendationToken}`
      }];
    }

    return {
      ...link,
      productData: {
        index,
        product,
        sourceType: this.sourceType,
        displayedProductIds: this.displayedProductIds
      }
    };
  }

  public trackByProduct(index: number, item: Product): string {
    return item.id;
  }

  /** Tenants can invoke this method in their templates when they overwrite the Core template. */
  public onProductClick(event: Event, product: Product, index: number): void {
    const renderEvent = {
      event,
      extra: {
        type: 'productCarrouselSlideClicked',
        productId: product.id
      },
      data: {
        index,
        product,
        sourceType: this.sourceType,
        displayedProductIds: this.displayedProductIds,
        ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier })

      },
    } as RenderEvent;

    // TODO Remove support for categoryV2 https://axinic.central.inditex.grp/jira/browse/CMS-7284
    if (CATEGORIES_SOURCES.includes(this.sourceType)) {
      renderEvent.extra!.categoryId = this.piece?.settings?.general.sourceId;
    }

    this.renderEventsService.events.next(renderEvent);
  }

  public onProductLinkClick(product: Product, index: number): void {
    const cmsEvent: CMSEvent<CMSProductCarouselEvent> = {
      name: 'productLink',
      type: 'click',
      data: {
        index,
        product,
        sourceType: this.sourceType,
        ...(this.piece?.settings?.general.identifier && { identifier: this.piece.settings.general.identifier })
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public onFirstView(): void {
    const event = new Event('idtEvent');
    const renderEvent = {
      event,
      extra: {
        type: 'productCarrouselOnViewport'
      }
    } as RenderEvent;
    this.renderEventsService.events.next(renderEvent);
  }

  public arrowClick(direction: 'prev' | 'next'): void {
    if (direction === 'next') {
      this.swiperComponent?.swiperRef.slideNext();
    } else {
      this.swiperComponent?.swiperRef.slidePrev();
    }
  }

  private onMediaChange(): void {
    const gridSize = this.gridSize;
    this.swiperConfig = {
      ...this.swiperConfig,
      slidesPerView: gridSize.numberOfColumns,
      slidesPerColumn: gridSize.numberOfRows,
      slidesPerGroup: Math.floor(gridSize.numberOfColumns) * gridSize.numberOfRows
    };
  }

  private externalProducts(): Observable<Product[]> {
    const hasIPOCProducts = Array.isArray(this.window.inditex?.iPOCProducts) && this.window.inditex!.iPOCProducts.length > 0;

    if (!hasIPOCProducts) {
      return of([] as Product[]);
    }

    return of(this.window.inditex!.iPOCProducts).pipe(
      filter((products) => Array.isArray(products)),
      map((products) => {
        const deduplicatedProductIds = Array.from(new Set(products!.map((product) => product.a_dataProduct.id.toString())));
        const reducedProductsIds = deduplicatedProductIds.slice(0, this.maxProducts);
        return this.sanitizeProducts(reducedProductsIds.join(','));
      }),
      switchMap((productIds: string) => {
        return this.productsService.productsDetailArray(productIds).pipe(
          map((products: Product[]) => products.sort((a, b) => productIds.indexOf(a.id) - productIds.indexOf(b.id)))
        );
      })
    );
  }

  private sanitizeProducts(productsIds: string): string {
    return productsIds.replace(/\s/g, '');
  }

  private getViewStyle(viewStyle?: string): string | undefined {
    switch (viewStyle) {
      case 'silhouette-image':
        return '_2_4';
      case 'lookbook':
        return '_2_10';
      case 'main':
        return '_1_1';
      case 'custom-media-id':
        return this.piece?.settings?.general.customMediaId;
      default:
        return undefined; // In this case return main medias
    }
  }

  private initConfig(): void {
    const styles = this.piece?.settings?.styles;
    const gridSize = this.gridSize;

    this.swiperConfig = {
      freeMode: this.isScrollFree,
      navigation: false,
      pagination: false,
      loop: this.isScrollPager && !!this.piece?.settings?.general.loop,
      spaceBetween: styles ? Number(styles.spaceBetweenProducts?.slice(0, -2) ?? 0) : undefined,
      slidesOffsetBefore: styles ? Number(styles.initialOffset?.slice(0, -2) ?? 0) : undefined,
      slidesOffsetAfter: styles ? Number(styles.finalOffset?.slice(0, -2) ?? 0) : undefined,
      slidesPerView: gridSize.numberOfColumns,
      slidesPerColumnFill: 'row',
      slidesPerColumn: gridSize.numberOfRows,
      slidesPerGroup: Math.floor(gridSize.numberOfColumns) * gridSize.numberOfRows
    };

    if (this.isScrollPager && this.piece?.settings?.general.displayComponentPager) {
      this.swiperConfig.pagination = this.getPaginationOptions();
    }
  }

  private filterSeasonalProducts(products: Product[]): Product[] {
    const regex = /-(.)/;
    const seasonalProducts = [];
    for (const product of products) {
      const match = product.fullReference?.match(regex);
      const season = match ? match[1] : null;
      if (season === this.piece?.settings?.general.filterSeasonalProducts) {
        seasonalProducts.push(product);
      }
    }
    return seasonalProducts;
  }

  private getPaginationOptions(): PaginationOptions {
    let bulletClass = 'swiper-pagination-bullet';
    if (this.isPagerStyleBox) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-box`;
    } else if (this.isPagerStyleSolid) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-solid`;
    } else if (this.isPagerStyleDashed) {
      bulletClass = `${bulletClass} swiper-pagination-bullet-dashed`;
    }
    return {
      clickable: true,
      renderBullet: () => {
        return `<button class="${bulletClass}"></button>`;
      },
    };
  }

  private getCategoryIdBySeoId$(seoId: string): Observable<string | undefined> {
    return this.contextService.getCategories$().pipe(
      map((categories) => CategoriesHelpers.flatCategories(categories)),
      map((categories) => categories.find((category) => category.seoId === seoId)?.id),
    );
  }

}
